import React, { useCallback, useState, useEffect, useRef } from "react"
import { useHistory } from "react-router-dom"
import Form from "@bappweb/form"
import Panel from "@bappweb/panel"
import Row from "@bappweb/row"
import Col from "@bappweb/col"
import TextField from "@bappweb/textfield"
import Button from "@bappweb/button"
import Checkbox from "@bappweb/checkbox"
import Link from "@bappweb/link"
import Captcha from "@smpu-web/nested/form/captcha"
import FormMessage from "@smpu-web/globalmodules/form-message/index"
import Page from "@smpu-web/globalmodules/page"
import Element from "@bappweb/element"
import { RegexpTypes, required, minLength, email } from "@bappweb/utils"
import {
  useGetTokenByTckn1Mutation,
  useGetCaptchaMutation,
  useRefreshCaptchaMutation,
  useApplyMutation
} from "../../../api/service"
import { createUUID } from "@smpu-web/utils/utils"
import "../assets/style.scss"
import "../../../../../assets/styles/style.scss"

function MainForm() {
  const history = useHistory()

  const getCid = () => {
    let search = window.location.search;
    let params = new URLSearchParams(search);

    return params.get('cid') ?? "AkbOrgnc"
  }

  const [guid, setGuid] = useState("")

  const [captchaValues, setCaptchaValues] = useState({
    tokenSessionId: "",
    captchaData: "",
    captcha: "",
    showCaptcha: false,
    captchaValidate: true
  })

  const [error, setError] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)

  const prev = useRef({ captchaValues }).current;

  const [getTokenByTckn1] = useGetTokenByTckn1Mutation()
  useEffect(() => {
    if (!prev.captchaValues.tokenSessionId && !captchaValues.tokenSessionId && guid !== "") {
      getTokenByTckn1(guid)
        .unwrap()
        .then((payload) => {
          if (!payload.IsError) {
            setError("")
            setCaptchaValues({
              ...captchaValues,
              tokenSessionId: payload.tokenSessionId
            })
          } else {
            setError(payload.ErrorMessage ? payload.ErrorMessage : "")
          }
        })
        .finally(() => {
          setIsFetching(false)
        })
    }
  },[guid]);

  const handleNameBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const { value } = e.target
      if ((value ?? "").length > 5 && value.length <= 100 && guid === "") {
        setGuid(createUUID())
      }

    },
    [guid]
  )

  const handleChange = useCallback(
    (e) => {
      const name = e.target.name
      const value = e.target.value
      switch (name) {
        case "nameSurname":
          setGuid("") 
          setCaptchaValues({
            ...captchaValues,
            tokenSessionId: "",
            captchaData: "",
            showCaptcha : false
          })     
          break;
        case "captcha":
          setError(null)
          setCaptchaValues({
            ...captchaValues,
            captcha: value
          })
          break
        default:
          break;
      }
    },
    [captchaValues, guid]
  )

  const [getCaptcha] = useGetCaptchaMutation()
  useEffect(() => {
    if (captchaValues.captchaData === "" && captchaValues.tokenSessionId !== "") {
      setIsFetching(true)
      getCaptcha({
        guid: guid,
        TokenSessionId: captchaValues.tokenSessionId
      })
        .unwrap()
        .then((payload) => {
          if (!payload.IsError) {
            setCaptchaValues({
              ...captchaValues,
              captchaData: payload.captchaData,
              showCaptcha: true,
              captchaValidate: true
            })
          } else {
            setError(payload.ErrorMessage ? payload.ErrorMessage : "")
          }
        })
        .finally(() => {
          setIsFetching(false)
        })
    }
  }, [guid, getCaptcha, captchaValues])

  const [refreshCaptcha] = useRefreshCaptchaMutation()
  const handleCaptchaRefresh = useCallback(() => {
    setIsFetching(true)
    refreshCaptcha({
      guid: guid,
      TokenSessionId: captchaValues.tokenSessionId
    })
      .unwrap()
      .then((payload) => {
        if (!payload.IsError) {
          setCaptchaValues({
            ...captchaValues,
            captchaData: payload.captchaData,
            captchaValidate: true
          })
        } else {
          setError(payload.ErrorMessage ? payload.ErrorMessage : "")
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
  }, [guid, captchaValues, refreshCaptcha])

  const [apply] = useApplyMutation()
  const handleSubmit = useCallback(
    (e?: React.BaseSyntheticEvent, values?: Record<string, any>) => {
      if (values && captchaValues.captcha !== "") {
        setIsFetching(true)
        apply({
          guid: guid,
          adSoyad: values.nameSurname,
          email: values.email,
          telefon: "00" + values.countryCode + values.phone.replace(/\s/g, ''),
          captcha: captchaValues.captcha.toUpperCase(),
          kvkk: values.checkboxKvkk,
          etk: true,
          cid: getCid(),
          TokenSessionId: captchaValues.tokenSessionId
        })
          .unwrap()
          .then((payload) => {
            if (!payload.IsError) {
              setError(null)
              history.push("/result/success")
            } else {
              setError(payload.ErrorMessage ? payload.ErrorMessage : "")
              window.scrollTo({
                top: 0
              })
              refreshCaptcha({
                guid: guid,
                TokenSessionId: captchaValues.tokenSessionId
              })
                .unwrap()
                .then((payload) => {
                  if (!payload.IsError) {
                    setCaptchaValues({
                      ...captchaValues,
                      captcha: "",
                      showCaptcha: true,
                      captchaData: payload.captchaData,
                      captchaValidate: false
                    })
                  } else {
                    setError(payload.ErrorMessage ? payload.ErrorMessage : "")
                  }
                })
                .finally(() => {
                  setIsFetching(false)
                })
            }
          })
          .finally(() => {
            setIsFetching(false)
          })
      }
    },
    [captchaValues, guid, apply, refreshCaptcha, history]
  )

  return (
    <Page>
      <Form onSubmit={handleSubmit} onChange={handleChange} grid={false}>
        <Panel hasFooter connected={false}>
          <FormMessage className={"form-msg-error"} error={error}></FormMessage>
          <Panel.Body>
            <Element type="h4" className="bapp-panel-body-card-description">
              Expat Banking Application Form
            </Element>
            <TextField
              label="Name Surname"
              type="text"
              name="nameSurname"
              maxLength={100}
              onBlur={handleNameBlur}
              validator={RegexpTypes.AlphaTr}
              validations={[required("Please enter your Name and Surname.")]}
            />
            <Row>
              <Col xs={4} md={4} lg={4} xl={4} className="col-left">
                <TextField
                  label="Country Code"
                  type="text"
                  name="countryCode"
                  className="phone-number-row"
                  maxLength={3}
                  validator={RegexpTypes.Numeric}
                  validations={[required("Please enter your Phone Number"),
                  minLength(1, "Please lengthen this text to 1 characters or more")]}
                />
              </Col>
              <Col xs={6} md={6} lg={6} xl={6} className="col-left">
                <TextField
                  label="Phone Number"
                  type="text"
                  name="phone"
                  className="phone-number-row"
                  maxLength={15}
                  validator={RegexpTypes.Numeric}
                  validations={[required("Please enter your Phone Number"),
                  minLength(7, "Please lengthen this text to 7 characters or more")]}
                />
              </Col>
            </Row>
            <TextField
              type="text"
              name="email"
              label="Email Address"
              maxLength={150}
              validations={[required("Please enter your Email Adress"), email("Please Enter a Valid Email Address.")]}
            />
            {captchaValues.showCaptcha && (
              <Captcha
                label="Security Code"
                id="captcha"
                type="text"
                name="captcha"
                placeholder="Security Code"
                captcha={captchaValues.captchaData}
                value={captchaValues.captcha}
                maxLength={4}
                maxRefreshCount={3}
                refreshClick={handleCaptchaRefresh}                
                validations={[required("Please enter the Security Code.")]}
              />
            )}
            <div className="checkbox-group">
              <Checkbox
                className="cb-position-fix"
                name="checkboxKvkk"
                validations={[required("Please tick the Checkbox.")]}
                validateOnSubmit={true}
                block>
                {"I have read the information about "}
                <Link
                  href="//www.akbank.com/Documents/KISISELVERILERINKORUNMASI_EN.pdf"
                  text={"The Law on Protection of Personal Data"}
                  inline
                  target="_blank"
                />
              </Checkbox>
            </div>
          </Panel.Body>
          <Panel.Footer>
            <Panel.RightFooter>
              <Button text="Send" type="submit" fetching={isFetching} disabled={isFetching} />
            </Panel.RightFooter>
          </Panel.Footer>
        </Panel>
      </Form>
    </Page>
  )
}

export default MainForm
